<template>
    <Component
        :is="mode === 'portrait' ? PortraitProfileCard : LandscapeProfileCard"
        :id="id"
        :loading="loading"
        :link-href="linkHref"
        :link-target="linkTarget"
        :profile="profile"
        :global-matching-enabled="globalMatchingEnabled"
        :compact-mode="compactMode"
        :border-style="borderStyle"
        :tooltip-price="tooltipPrice"
        @click="$emit('click', $event)"
    >
        <template v-if="$slots['badges']" #badges>
            <slot name="badges" />
        </template>
        <template v-if="$slots['top-name']" #top-name>
            <slot name="top-name" />
        </template>
        <template v-if="$slots['extra-name']" #extra-name>
            <slot name="extra-name" />
        </template>
        <template v-if="$slots['main-body']" #main-body>
            <slot name="main-body" />
        </template>
        <template v-if="$slots['secondary-body']" #secondary-body>
            <slot name="secondary-body" />
        </template>
        <template v-if="$slots['extra-actions']" #extra-actions>
            <slot name="extra-actions" />
        </template>
        <template v-if="$slots['actions']" #actions>
            <slot name="actions" />
        </template>
    </Component>
</template>

<script setup lang="ts">
    import {type VNode} from 'vue';
    import LandscapeProfileCard from './landscape/LandscapeProfileCard.vue';
    import PortraitProfileCard from './portrait/PortraitProfileCard.vue';
    import type {ProfileCardProps} from './types';

    withDefaults(
        defineProps<
            ProfileCardProps & {
                mode?: 'portrait' | 'landscape';
            }
        >(),
        {
            mode: 'portrait',
            linkTarget: '_self',
        },
    );

    defineEmits<{
        click: [event: Event];
    }>();

    defineSlots<{
        /** Use this slot to insert badges. */
        badges?: () => VNode;
        /** Use this slot to insert custom content above the freelancer name. */
        'top-name'?: () => VNode;
        /** Use this slot to display additional content beside the freelancer name. */
        'extra-name'?: () => VNode;
        /** Use this slot to insert custom content below the freelancer profile information. */
        'main-body'?: () => VNode;
        /** Use this slot to insert additional content below the main-body on portrait mode and on the right on landscape mode. */
        'secondary-body'?: () => VNode;
        /** Use this slot to insert custom buttons on portrait mode.
         * On landscape mode, this slot has to be used in combination with the `ProfileCardLandscapeActions` component.
         * On portrait mode, this slot has to be used in combination with the `VJoyButton` components in medium size. */
        actions?: () => VNode;
        /** Use this slot to insert custom buttons on the top right of the card. This slot is only available on portrait mode. */
        'extra-actions'?: () => VNode;
    }>();
</script>
